import {
  Badge,
  Button,
  Callout,
  Card,
  Flex,
  Heading,
  Spinner,
  Text,
} from "@radix-ui/themes";

import {
  mapReplacementHistory,
  type OrderVersionHistoryViewModel,
} from "./OrderDetailDialog.utils";
import { DetailFields, Section } from "./OrderDetailDialogSummary.pres";
import type { ReplacementHistory } from "./replacementChains";

export interface OrderDetailDialogReplacementHistoryPresentationProps {
  history?: ReplacementHistory;
  isLoading: boolean;
  isFetching: boolean;
  onRetry: () => void;
}

const getHistoryTagColor = (
  tag: string,
): "blue" | "gray" | "orange" | "red" | "yellow" => {
  if (tag === "Current") {
    return "yellow";
  }
  if (tag === "Clicked") {
    return "blue";
  }
  if (tag === "Rejected attempt") {
    return "red";
  }
  if (
    tag === "Ambiguous branch" ||
    tag === "Earliest loaded" ||
    tag === "Latest loaded"
  ) {
    return "orange";
  }
  return "gray";
};

const HistoryVersionCard = ({
  version,
}: {
  version: OrderVersionHistoryViewModel;
}) => (
  <Card
    size="1"
    variant="surface"
    data-history-kind={version.kind}
    data-testid={`order-version-${version.key}`}
  >
    <Flex direction="column" gap="3">
      <Flex align="center" justify="between" gap="2" wrap="wrap">
        <Heading as="h4" size="2">
          {version.title}
        </Heading>
        <Flex align="center" gap="1" wrap="wrap">
          {version.tags.map((tag) => (
            <Badge key={tag} color={getHistoryTagColor(tag)} variant="soft">
              {tag}
            </Badge>
          ))}
        </Flex>
      </Flex>
      <Text size="1" color="gray">
        {version.changedFields}
      </Text>
      <DetailFields fields={version.fields} />
    </Flex>
  </Card>
);

export const OrderDetailDialogReplacementHistoryPresentation = ({
  history,
  isLoading,
  isFetching,
  onRetry,
}: OrderDetailDialogReplacementHistoryPresentationProps) => {
  const viewModel = history ? mapReplacementHistory(history) : undefined;

  return (
    <Section title="Replacement history">
      <Text size="2" color="gray">
        This is a bounded sequence of loaded order versions. It is not a
        complete status-event or execution history.
      </Text>
      {isLoading ? (
        <Flex align="center" gap="2" role="status">
          <Spinner size="1" />
          <Text size="2" color="gray">
            Loading replacement history…
          </Text>
        </Flex>
      ) : viewModel ? (
        <Flex direction="column" gap="3">
          <Card size="1" variant="surface">
            <Flex direction="column" gap="1">
              <Text size="1" color="gray">
                Loaded version status sequence
              </Text>
              <Text size="2" className="atd-numeric">
                {viewModel.statusSequence}
              </Text>
            </Flex>
          </Card>
          {!viewModel.isComplete ? (
            <Callout.Root color="orange" role="status">
              <Callout.Text>
                Replacement history may be incomplete.
              </Callout.Text>
              {viewModel.issueMessages.map((message) => (
                <Text key={message} as="p" size="2">
                  {message}
                </Text>
              ))}
              <Button
                type="button"
                size="1"
                variant="soft"
                color="orange"
                onClick={onRetry}
                loading={isFetching}
              >
                Retry history
              </Button>
            </Callout.Root>
          ) : null}
          <Flex direction="column" gap="2">
            {viewModel.versions.map((version) => (
              <HistoryVersionCard key={version.key} version={version} />
            ))}
          </Flex>
        </Flex>
      ) : (
        <Text size="2" color="gray">
          No replacement-version detail is available.
        </Text>
      )}
    </Section>
  );
};
